<template>
  <v-container class="py-7">
    <v-row>
      <v-col cols="12">
        <div class="title-container">
          <div class="title-block">
            <span class="page-title">Detalhes da conversa</span>
            <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbs"></v-breadcrumbs>
          </div>
          <div class="button-group">
            <v-btn color="primary" outlined elevation="0" to="/conversas">Voltar</v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col cols="12">
        <v-skeleton-loader type="article, list-item-three-line, actions"></v-skeleton-loader>
      </v-col>
    </v-row>

    <template v-else>
      <v-row>
        <v-col cols="12" md="4">
          <v-card class="card-form-outlined">
            <v-card-title class="card-group-title">Contexto</v-card-title>
            <v-card-text>
              <div class="chat-context-list">
                <div>
                  <span>Aluno</span>
                  <strong>{{ conversation.student ? conversation.student.name : "N/A" }}</strong>
                </div>
                <div>
                  <span>Turma</span>
                  <strong>{{ conversation.class ? conversation.class.name : "N/A" }}</strong>
                </div>
                <div>
                  <span>Canal</span>
                  <strong>{{ channelLabel(conversation.channel) }}</strong>
                </div>
                <div>
                  <span>Status</span>
                  <v-chip small label :color="statusColor(conversation.status)" text-color="white">
                    {{ statusLabel(conversation.status) }}
                  </v-chip>
                </div>
              </div>

              <v-btn class="mt-4" block color="primary" outlined elevation="0" @click="closeConversation" :disabled="conversation.status === 'closed'">
                Encerrar conversa
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="8">
          <v-card class="chat-panel">
            <v-card-title class="card-group-title">Mensagens</v-card-title>
            <v-card-text>
              <div class="chat-thread">
                <div
                  v-for="message in messages"
                  :key="message.id"
                  class="chat-message"
                  :class="{ 'chat-message-own': message.sender_user_id === currentUserId }"
                >
                  <div class="chat-bubble">
                    <small>{{ senderLabel(message) }} · {{ message.created_at | defaultDate("DD/MM/YYYY HH:mm") }}</small>
                    <p>{{ message.body }}</p>
                  </div>
                </div>
                <div v-if="!messages.length" class="chat-empty">Nenhuma mensagem registrada.</div>
              </div>

              <v-form class="chat-form" @submit.prevent="sendMessage">
                <v-textarea
                  dense
                  outlined
                  rows="3"
                  label="Mensagem"
                  v-model="message"
                  :disabled="conversation.status === 'closed'"
                  hide-details="auto"
                ></v-textarea>
                <v-btn class="mt-3" type="submit" color="primary" elevation="0" :loading="sending" :disabled="conversation.status === 'closed'">
                  Enviar
                </v-btn>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "ChatDetails",

  data: () => ({
    loading: false,
    sending: false,
    message: "",
    conversation: {
      messages: [],
    },
  }),

  computed: {
    breadcrumbs() {
      return [
        { text: "Homepage", disabled: false, to: "/home", exact: true },
        { text: "Conversas", disabled: false, to: "/conversas", exact: true },
        { text: "Detalhes da conversa", disabled: true, exact: true },
      ];
    },

    messages() {
      return this.conversation.messages || [];
    },

    currentUserId() {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        return user.id || (user.data && user.data.id);
      } catch (error) {
        return null;
      }
    },
  },

  created() {
    this.load();
  },

  methods: {
    async load() {
      this.loading = true;

      try {
        const result = await axios.get(`/api/v1/chat/conversations/${this.$route.params.id}`);
        this.conversation = result.data.content;
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 3000 });
      } finally {
        this.loading = false;
      }
    },

    async sendMessage() {
      if (!this.message.trim()) return;

      this.sending = true;

      try {
        await axios.post(`/api/v1/chat/conversations/${this.$route.params.id}/messages`, { body: this.message.trim() });
        this.message = "";
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 3000 });
      } finally {
        this.sending = false;
      }
    },

    async closeConversation() {
      await axios.patch(`/api/v1/chat/conversations/${this.$route.params.id}/status`, { status: "closed" });
      await this.load();
    },

    senderLabel(message) {
      if (message.sender_user && message.sender_user.name) return message.sender_user.name;
      if (message.sender_type === "answerable") return "Responsável";
      if (message.sender_type === "employee") return "Equipe da escola";
      if (message.sender_type === "admin") return "Administração";
      return "Usuário";
    },

    channelLabel(channel) {
      const labels = {
        class_team: "Equipe da turma",
        teacher: "Professores",
        assistant: "Auxiliares",
        coordination: "Coordenação",
      };

      return labels[channel] || channel || "Conversa";
    },

    statusLabel(status) {
      const labels = {
        open: "Aberta",
        waiting_school: "Aguardando escola",
        waiting_answerable: "Aguardando responsável",
        closed: "Encerrada",
      };

      return labels[status] || status;
    },

    statusColor(status) {
      if (status === "closed") return "grey";
      if (status === "waiting_school") return "warning";
      if (status === "waiting_answerable") return "primary";
      return "success";
    },
  },
};
</script>

<style scoped lang="scss">
.chat-context-list {
  display: grid;
  gap: 10px;
}

.chat-context-list div {
  border: 1px solid #e5eaf2;
  border-radius: 8px;
  padding: 12px;
}

.chat-context-list span {
  color: #94a3b8;
  display: block;
  font-size: 11px;
  font-weight: 800;
  margin-bottom: 4px;
}

.chat-context-list strong {
  color: #0f172a;
}

.chat-panel {
  border: 1px solid #e5eaf2;
  border-radius: 8px;
  box-shadow: none !important;
}

.chat-thread {
  display: grid;
  gap: 12px;
  max-height: 520px;
  overflow-y: auto;
  padding: 4px;
}

.chat-message {
  display: flex;
}

.chat-message-own {
  justify-content: flex-end;
}

.chat-bubble {
  background: #f6f8fb;
  border: 1px solid #e5eaf2;
  border-radius: 8px;
  max-width: min(620px, 85%);
  padding: 12px;
}

.chat-message-own .chat-bubble {
  background: #edf4ff;
  border-color: #cfe1ff;
}

.chat-bubble small {
  color: #64748b;
  display: block;
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 6px;
}

.chat-bubble p {
  color: #0f172a;
  margin: 0;
  white-space: pre-wrap;
}

.chat-empty {
  color: #64748b;
  padding: 24px;
  text-align: center;
}

.chat-form {
  margin-top: 18px;
}
</style>
